import { defineStore } from 'pinia'
import type { Note } from '../../types/content'
import type { SortByOption } from '../../types/sorting'
import { useEntityStore } from '../composables/useEntityStore'

export const useNotesStore = defineStore('notes', () => {
  /**
   * Creates a preview of the given content string.
   * If the content is longer than previewLength, it will be truncated
   * and '...' will be appended to the end of the string.
   * @param {string | undefined} content - The string to create a preview of.
   * @returns {string} A preview of the content string.
   */
  function createPreview(content: string | undefined): string {
    if (!content) return ''
    const safeContent = content.replace(/(<([^>]+)>)/gi, ' ')
    const previewLength = 128
    return `${safeContent.substring(0, previewLength)}${
      safeContent.length > previewLength ? '...' : ''
    }`
  }

  const {
    items: notes,
    sortBy,
    sortOrder,
    sortItems,
    getById,
    add,
    update,
    remove,
    removeBulk,
    restore,
    restoreBulk,
    moveToContext,
    toggleFavourite,
    toggleFavouriteBulk,
    setSortBy: _setSortBy,
    toggleSortOrder,
    loadSortPreferences
  } = useEntityStore<Note>({
    table: 'notes',
    sortPreferencePrefix: 'notes',
    sortStrategies: {
      title: (a: Note, b: Note) => b.title.localeCompare(a.title) as number
    },
    beforeUpdate: (updates) => ({
      ...updates,
      preview: createPreview(updates.content)
    })
  })

  const sortedFavouriteNotes = computed(() => {
    return sortItems(notes.value.filter((n) => n.favourite))
  })

  const sortedRegularNotes = computed(() => {
    return sortItems(notes.value.filter((n) => !n.favourite))
  })

  /**
   * Retrieves a note by its ID.
   * @param id - The note ID
   * @returns The note or null if not found
   */
  async function getNoteById(id: string): Promise<Note | null> {
    return getById(id)
  }

  /**
   * Adds a new note with the given title in the current context.
   * @param newTitle - The title of the new note
   * @returns The created note or null on failure
   */
  async function addNewNote(newTitle: string): Promise<Note | null> {
    return add({ title: newTitle, labels: [] } as Partial<Note>)
  }

  async function importNote(
    title: string,
    content: string
  ): Promise<Note | null> {
    return add({ title, content, labels: [] } as Partial<Note>)
  }

  /**
   * Updates a note in the database.
   * @param updates - An object containing the fields to update.
   * @returns A promise that resolves when the note is updated.
   */
  async function updateNote(
    updates: Partial<Note & { id: string }>
  ): Promise<void> {
    return update(updates)
  }

  /**
   * Removes a note from the database.
   * @param id - The ID of the note to remove.
   */
  async function removeNote(id: string): Promise<void> {
    return remove(id)
  }

  /**
   * Removes multiple notes.
   * @param ids - Array of note IDs to remove.
   */
  async function removeNotesInBulk(ids: string[]): Promise<void> {
    return removeBulk(ids)
  }

  /**
   * Restores a previously deleted note.
   * @param id - The ID of the note to restore.
   */
  async function restoreNote(id: string): Promise<void> {
    return restore(id)
  }

  /**
   * Restores multiple previously deleted notes.
   * @param ids - Array of note IDs to restore.
   */
  async function restoreNotesInBulk(ids: string[]): Promise<void> {
    return restoreBulk(ids)
  }

  /**
   * Moves notes to a different context.
   * @param ids - Array of note IDs to move.
   * @param contextId - Target context ID.
   */
  async function moveNotesToAnotherContext(
    ids: string[],
    contextId: string
  ): Promise<void> {
    return moveToContext(ids, contextId)
  }

  /**
   * Sets the sort option for notes.
   * @param option - The sort option to set.
   */
  async function setSortBy(option: SortByOption): Promise<void> {
    return _setSortBy(option)
  }

  /**
   * Toggles the favourite status of a note.
   * @param id - The note ID.
   */
  async function toggleFavouriteNote(id: string): Promise<void> {
    return toggleFavourite(id)
  }

  /**
   * Toggles the favourite status of multiple notes.
   * @param ids - Array of note IDs.
   */
  async function toggleFavouriteNotesInBulk(ids: string[]): Promise<void> {
    return toggleFavouriteBulk(ids)
  }

  return {
    notes,
    sortBy,
    sortOrder,
    sortedFavouriteNotes,
    sortedRegularNotes,
    getNoteById,
    addNewNote,
    importNote,
    updateNote,
    removeNote,
    moveNotesToAnotherContext,
    removeNotesInBulk,
    restoreNote,
    restoreNotesInBulk,
    setSortBy,
    toggleSortOrder,
    loadSortPreferences,
    toggleFavouriteNote,
    toggleFavouriteNotesInBulk
  }
})
